/**
 * The compare mode and the two role-named inputs of a finished comparison,
 * read back from its parts' stored inputs (spec §12.1, §12.9) — what the hub
 * drawer shows as "Compare against · Design" and "Design: <layer>",
 * "As-built survey: <layer>". Pure: no React, no i18n.
 *
 * The cut & fill part is the source of truth: it stores `compareKind` and the
 * `topLayerId` / `bottomLayerId` the roles map onto (A = top, B = bottom, as
 * in the dialog). Without it (it failed to start, or its input is gone) the
 * deviation part speaks: a deviation map is design-only, its reference is the
 * design and its source the as-built survey.
 */
import { roleSlots, type CompareKind } from './availability';

export interface RunRole {
  /** The `project` i18n key naming the role ("compareSurfaces.roles.design"). */
  labelKey: string;
  layerId: string | null;
}

export interface ComparisonRoles {
  kind: CompareKind;
  a: RunRole;
  b: RunRole;
}

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (typeof v === 'object' && v !== null ? (v as Rec) : {});
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

export function comparisonRoles(cutFillInput: unknown, deviationInput: unknown): ComparisonRoles | null {
  const sd = rec(cutFillInput);
  const top = str(sd.topLayerId);
  const bottom = str(sd.bottomLayerId);
  if (top || bottom) {
    const kind: CompareKind = rec(sd.params).compareKind === 'survey' ? 'survey' : 'design';
    const [a, b] = roleSlots(kind);
    return { kind, a: { labelKey: a.labelKey, layerId: top }, b: { labelKey: b.labelKey, layerId: bottom } };
  }
  const dev = rec(deviationInput);
  const reference = str(dev.referenceLayerId);
  const source = str(dev.sourceLayerId);
  if (reference || source) {
    const [a, b] = roleSlots('design');
    return { kind: 'design', a: { labelKey: a.labelKey, layerId: reference }, b: { labelKey: b.labelKey, layerId: source } };
  }
  return null;
}
